import { Component } from '@theme/component';

/**
 * Collects a locker preference through the native cart form.
 * @typedef {object} Refs
 * @property {HTMLSelectElement} method
 * @property {HTMLInputElement} locker
 * @property {HTMLInputElement} emptyLocker
 * @property {HTMLDivElement} lockerFields
 * @extends {Component<Refs>}
 */
class JaxieDeliveryPreference extends Component {
  requiredRefs = ['method', 'locker', 'emptyLocker', 'lockerFields'];

  connectedCallback() {
    super.connectedCallback();
    this.changeMethod();
    // The checkout control may be outside cart-form (cart drawer/page summary).
    // Persist cart attributes before allowing checkout to avoid losing the locker.
    this.checkoutButton = this.closest('.cart__ctas')?.querySelector('.cart__checkout-button');
    this.checkoutButton?.addEventListener('click', this.checkoutWithPreference, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.checkoutButton?.removeEventListener('click', this.checkoutWithPreference, true);
  }

  checkoutWithPreference = async (event) => {
    if (this.saving) {
      event.preventDefault();
      return;
    }
    // Handle regular checkout buttons; never interfere with external links.
    const method = this.refs.method.value;
    const locker = this.refs.locker.value.trim();
    if (method === 'Pudo' && !locker) {
      event.preventDefault();
      this.refs.locker.setCustomValidity(this.dataset.requiredMessage || 'Enter your preferred Pudo locker.');
      this.refs.locker.reportValidity();
      this.refs.locker.focus();
      return;
    }
    event.preventDefault();
    this.saving = true;
    const errorMessage = this.querySelector('[data-jaxie-delivery-error]');
    if (errorMessage) errorMessage.hidden = true;
    if (this.checkoutButton) this.checkoutButton.disabled = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Delivery preference': method,
            'Preferred Pudo locker': method === 'Pudo' ? locker : ''
          }
        })
      });
      if (!response.ok) throw new Error('Unable to save delivery preference');
      window.location.assign(root + 'checkout');
    } catch (error) {
      this.saving = false;
      if (this.checkoutButton) this.checkoutButton.disabled = false;
      const errorMessage = this.querySelector('[data-jaxie-delivery-error]');
      if (errorMessage) errorMessage.hidden = false;
    }
  };

  changeMethod() {
    const isPudo = this.refs.method.value === 'Pudo';
    this.dataset.pudo = String(isPudo);
    this.refs.lockerFields.hidden = !isPudo;
    this.refs.locker.required = isPudo;
    this.refs.locker.disabled = !isPudo;
    this.refs.emptyLocker.disabled = isPudo;
    this.updateLocker();
  }

  updateLocker() {
    const missingLocker = this.refs.method.value === 'Pudo' && !this.refs.locker.value.trim();
    this.refs.locker.setCustomValidity(missingLocker ? this.dataset.requiredMessage || '' : '');
  }
}

if (!customElements.get('jaxie-delivery-preference')) {
  customElements.define('jaxie-delivery-preference', JaxieDeliveryPreference);
}
